/*
 * T55. This imported `API_URL` from `./api`, which is `legacy` — and that was the blocker every
 * proposal to move this file tripped over: `src/shared/` may not depend on the legacy layer, so the
 * move created a violation the moment it landed.
 *
 * It dissolved rather than being solved. T51 shrank `src/utils/api.ts` to a single re-export of
 * `env.VITE_API_URL`, so reading `env` directly is the same value one hop shorter, and `src/env.ts`
 * matches no layer pattern in `eslint.config.js:25-33` — it is the configuration boundary, below
 * all of them. One line, where the ticket budgeted a redesign.
 */
import { env } from '@/env';

export interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined | null>;
  body?: any;
}

export async function fetchClient<T = any>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { params, headers, body, ...rest } = options;

  let url = endpoint.startsWith('http') ? endpoint : `${env.VITE_API_URL}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
  }

  const response = await fetch(url, {
    ...rest,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...Object.fromEntries(new Headers(headers as any).entries()),
    },
    body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
  });

  // Handle errors
  if (!response.ok) {
    let errorMessage = `Error: ${response.status} ${response.statusText}`;
    let errorCode: unknown;
    let errorDetails: unknown;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      errorCode = errorData?.code;
      errorDetails = errorData?.data;
    } catch (e) {
      // If not JSON, use the status text
    }
    // NEX-1357: keep the API's machine-readable code so callers can choose copy by code;
    // the message is unchanged (src/shared/http.test.ts pins it).
    // NEX-1169: and the refusal's `data` when it sends one (e.g. the depósitos a payout batch left
    // out, `STORAGE_PAYOUT_NO_PAYABLE_DEPOTS`) — unparsed here; the caller validates it with its contract.
    const error = new Error(errorMessage) as Error & { code?: string; data?: unknown };
    if (typeof errorCode === 'string') error.code = errorCode;
    if (errorDetails !== undefined && errorDetails !== null) error.data = errorDetails;
    throw error;
  }

  try {
    const text = await response.text();
    return text ? JSON.parse(text) : {} as T;
  } catch {
    return {} as T;
  }
}

export const http = {
  get: <T = any>(url: string, options?: FetchOptions) =>
    fetchClient<T>(url, { ...options, method: 'GET' }),

  post: <T = any>(url: string, body?: any, options?: FetchOptions) =>
    fetchClient<T>(url, { ...options, method: 'POST', body }),

  put: <T = any>(url: string, body?: any, options?: FetchOptions) =>
    fetchClient<T>(url, { ...options, method: 'PUT', body }),

  patch: <T = any>(url: string, body?: any, options?: FetchOptions) =>
    fetchClient<T>(url, { ...options, method: 'PATCH', body }),

  delete: <T = any>(url: string, options?: FetchOptions) =>
    fetchClient<T>(url, { ...options, method: 'DELETE' }),
};
